/**
 * App Router
 * ──────────
 * Routes mapped from Bible v2.0 Section 2: Complete User Journey
 * Protected routes require auth; public routes don't.
 */
import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/lib/auth-store';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import NotificationService from '@/components/NotificationService';
import CelebrationOverlay from '@/components/gamification/CelebrationOverlay';
import WhatsNewOverlay from '@/components/WhatsNewOverlay';

// Lazy load with retry (fixes chunk loading errors after deploy)
function lazyRetry(importFn: () => Promise<any>) {
  return React.lazy(() => importFn().catch(() => {
    window.location.reload();
    return importFn();
  }));
}

// ─── Lazy-loaded screens ─────────────────────────────────────────────────────

// Public
const Landing = lazyRetry(() => import('@/screens/Landing'));
const Login = lazyRetry(() => import('@/screens/Login'));
const PrivacyPolicy = lazyRetry(() => import('@/screens/PrivacyPolicy'));
const Learn = lazyRetry(() => import('@/screens/Learn'));

// Bib reference harness. The import sits inside the DEV branch on purpose:
// guarding only the <Route> still leaves the dynamic import in the graph, and
// Rollup emits the chunk into the production build. Vite substitutes
// import.meta.env.DEV with false when building, so this whole branch — and the
// chunk — disappears.
const BibReference = import.meta.env.DEV
  ? lazyRetry(() => import('@/screens/BibReference'))
  : null;

// Onboarding
const ObjectiveSelector = lazyRetry(() => import('@/screens/ObjectiveSelector'));
const Intake = lazyRetry(() => import('@/screens/Intake'));
const Subscription = lazyRetry(() => import('@/screens/Subscription'));

// Daily coaching (mobile)
const DailyBibScreen = lazyRetry(() => import('@/screens/DailyBibScreen'));
const WeekBib = lazyRetry(() => import('@/screens/WeekBib'));
const CoachPaper = lazyRetry(() => import('@/screens/CoachPaper'));
const ProgressPaper = lazyRetry(() => import('@/screens/ProgressPaper'));
const CheckInPaper = lazyRetry(() => import('@/screens/CheckInPaper'));
const MePaper = lazyRetry(() => import('@/screens/MePaper'));
const EverythingElse = lazyRetry(() => import('@/screens/EverythingElse'));
const FoodPaper = lazyRetry(() => import('@/screens/FoodPaper'));

// Desktop
const DesktopOverview = lazyRetry(() => import('@/screens/DesktopOverview'));
const DesktopCalendar = lazyRetry(() => import('@/screens/DesktopCalendar'));

// Admin
const Admin = lazyRetry(() => import('@/screens/Admin'));
const AdminSettings = lazyRetry(() => import('@/screens/AdminSettings'));

// Settings
const KnowledgeGalaxy = lazyRetry(() => import('@/screens/KnowledgeGalaxy'));

// ─── Loading fallback ────────────────────────────────────────────────────────

const LoadingScreen: React.FC = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#0A0A0A',
    color: '#5A5A5A',
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    letterSpacing: '0.1em',
  }}>
    KADER AI
  </div>
);

// ─── Protected route wrapper ─────────────────────────────────────────────────

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, loading, accessDenied, accessStatus } = useAuthStore();

  if (loading || accessStatus === 'checking') return <LoadingScreen />;
  if (!user || accessDenied) return <Navigate to="/login" replace />;
  if (accessStatus === 'expired') return <Navigate to="/subscription" replace />;

  return <>{children}</>;
};

// ─── App Router ──────────────────────────────────────────────────────────────

// In PWA standalone mode, redirect to "/" on fresh app launch (not in-app nav).
// Mobile OS restores last-visited URL, which may be /profile or /coach.
function PwaStartRedirect() {
  const navigate = React.useMemo(() => {
    // Check once on mount: is this a fresh PWA launch?
    if (typeof window === 'undefined') return false;
    const isPwa = window.matchMedia('(display-mode: standalone)').matches;
    if (isPwa && window.location.pathname !== '/' && !sessionStorage.getItem('kader-pwa-active')) {
      sessionStorage.setItem('kader-pwa-active', '1');
      return true;
    }
    sessionStorage.setItem('kader-pwa-active', '1');
    return false;
  }, []);

  if (navigate) return <Navigate to="/" replace />;
  return null;
}

export const AppRouter: React.FC = () => (
  <ErrorBoundary screenName="App">
    <BrowserRouter>
      <PwaStartRedirect />
      <NotificationService />
      <CelebrationOverlay />
      <WhatsNewOverlay />
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Public routes */}
          <Route path="/welcome" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />

          {/* Bib reference harness — dev only, so it never ships. Unauthenticated
              on purpose: it renders fixtures, never a real athlete's data. */}
          {import.meta.env.DEV && BibReference && (
            <>
              <Route path="/bib-reference" element={<BibReference />} />
              <Route path="/bib-reference/:id" element={<BibReference />} />
            </>
          )}

          {/* Protected routes — daily coaching */}
          {/* The daily bib is the start screen; the brief it was built from now
              sits one tap away behind "Everything else". */}
          <Route path="/" element={<ProtectedRoute><ErrorBoundary screenName="Daily Bib"><DailyBibScreen /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/day/:date" element={<ProtectedRoute><ErrorBoundary screenName="Daily Bib"><DailyBibScreen /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/coach" element={<ProtectedRoute><ErrorBoundary screenName="Coach"><CoachPaper /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/food" element={<ProtectedRoute><ErrorBoundary screenName="Food"><FoodPaper /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/post-session" element={<ProtectedRoute><ErrorBoundary screenName="Check in"><CheckInPaper /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/everything" element={<ProtectedRoute><ErrorBoundary screenName="Everything else"><EverythingElse /></ErrorBoundary></ProtectedRoute>} />

          {/* Protected routes — onboarding & subscription */}
          <Route path="/onboarding" element={<ProtectedRoute><ErrorBoundary screenName="Objective Selector"><ObjectiveSelector /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/intake" element={<ProtectedRoute><ErrorBoundary screenName="Intake"><Intake /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/subscription" element={<ProtectedRoute><Subscription /></ProtectedRoute>} />
          <Route path="/plan" element={<ProtectedRoute><ErrorBoundary screenName="Week"><WeekBib /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/progress" element={<ProtectedRoute><ErrorBoundary screenName="Progress"><ProgressPaper /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/brain" element={<ProtectedRoute><ErrorBoundary screenName="Knowledge Galaxy"><KnowledgeGalaxy /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/learn" element={<ProtectedRoute><ErrorBoundary screenName="Learn"><Learn /></ErrorBoundary></ProtectedRoute>} />

          {/* Protected routes — desktop */}
          <Route path="/desktop" element={<ProtectedRoute><ErrorBoundary screenName="Desktop"><DesktopOverview /></ErrorBoundary></ProtectedRoute>} />
          <Route path="/calendar" element={<ProtectedRoute><ErrorBoundary screenName="Calendar"><DesktopCalendar /></ErrorBoundary></ProtectedRoute>} />

          {/* Protected routes — admin */}
          <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute><AdminSettings /></ProtectedRoute>} />

          {/* Protected routes — settings */}
          <Route path="/profile" element={<ProtectedRoute><ErrorBoundary screenName="Me"><MePaper /></ErrorBoundary></ProtectedRoute>} />

          {/* Fallback */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  </ErrorBoundary>
);
